/** 글자로 옮겨도 되는 값만 글자로 — 객체는 `fallback`(「[object Object]」를 화면에 내지 않는다) */
const scalarText = (value: unknown, fallback = ''): string =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : fallback

/**
 * 레시피 편집기(V2) — 판정·조작(DOM 없음). 백엔드 `app/resources/admin/recipe_editor_model.js`(core@d50b8cf4)를 TS 로 옮겼다.
 *
 * 새 형식(Opentrons protocol/8 · 편집층 `designerApplication.data`) 레시피를 **읽고 고친다.** 화면은 이 함수들만 불러 문서를 바꾼다.
 *
 * 🔴 **안 건드린 칸은 그대로다.** 모든 조작은 문서를 복사한 뒤 **그 칸 하나만** 바꾼다 — 모르는 칸을 버리지 않고, 순서를 다시
 *    매기지 않는다. 저장(`PUT /recipes/{id}`)이 새 버전 «전체 교체»라 이 성질이 곧 데이터 보존이다(CLAUDE.md PUT 절).
 * 🔴 **셋업 명령(랩웨어·시약 자리)을 쓰는 곳은 `writeSetup` 한 곳이다**(서버 결정 대기 S-326 이 바뀌면 여기만 바뀐다).
 * ⚠ **작업 명령(흡입·분주 …)은 여기서 다시 만들지 않는다** — 서버 `POST /recipes/regenerate` 가 만들고 `replaceWork` 가 받는다.
 * ⚠ 검증 · 요약 · 조합의 판정은 전부 서버다. 여기는 문서를 다루는 손뿐이다.
 */

/* ── 문서 모양(아는 칸만 · 나머지는 인덱스로 그대로 실려 다닌다) ─────────────────────────── */

export interface EditorCommand {
  commandType: string
  key?: string
  intent?: string
  params: Record<string, unknown>
  [extra: string]: unknown
}

export interface LabwareDefinition {
  namespace: string
  version: number
  parameters: { loadName: string; isTiprack?: boolean; [extra: string]: unknown }
  metadata?: { displayName?: string; tags?: unknown[]; [extra: string]: unknown }
  wells?: Record<string, Record<string, unknown>>
  dimensions?: { xDimension: number; yDimension: number; [extra: string]: unknown }
  [extra: string]: unknown
}

export interface EditorLiquid {
  displayName?: string
  description?: string
  displayColor?: string
  [extra: string]: unknown
}

export interface EditorStep {
  key: string
  [extra: string]: unknown
}

export interface DesignerData {
  recipe?: Record<string, unknown>
  reagents?: Record<string, Record<string, unknown>>
  labware?: Record<string, { definitionUri?: string; [extra: string]: unknown }>
  process?: Record<string, Record<string, unknown>>
  [extra: string]: unknown
}

export interface EditorDoc {
  $otSharedSchema: string
  commands: EditorCommand[]
  commandAnnotations?: unknown[]
  labwareDefinitions?: Record<string, LabwareDefinition>
  liquids?: Record<string, EditorLiquid>
  robot?: { deckId?: string; [extra: string]: unknown }
  designerApplication: {
    name?: string
    version?: string
    data: DesignerData
    [extra: string]: unknown
  }
  [extra: string]: unknown
}

export const SETUP_COMMANDS: ReadonlySet<string> = new Set([
  'loadLabware',
  'loadLiquid',
  'loadModule',
  'loadPipette',
])

/** 셋업 명령인가 — 이 넷 밖은 전부 작업 명령이다(서버 `opentrons.SETUP` 과 같은 넷) */
export function isSetup(command: EditorCommand): boolean {
  return SETUP_COMMANDS.has(command.commandType)
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const dataOf = (doc: EditorDoc): DesignerData => doc.designerApplication.data

/** 새 형식인가 — 오픈트론 봉투와 편집층이 둘 다 있어야 한다 */
export function isNewFormat(doc: unknown): doc is EditorDoc {
  if (!isObject(doc)) return false
  const app = doc['designerApplication']
  return (
    typeof doc['$otSharedSchema'] === 'string' &&
    Array.isArray(doc['commands']) &&
    isObject(app) &&
    isObject(app['data'])
  )
}

export type OpenResult =
  | { doc: EditorDoc; problem: null; name: string | null; note: string | null }
  | { doc: null; problem: string; name: null; note: null }

/**
 * 문서를 여는 자리 — 새 형식이 아니면 까닭을 돌려준다(지어내 고치지 않는다).
 * 저장 봉투(`{name, schema_version, payload}`)면 본문을 열고 이름을 같이 준다. 봉투의 판과 본문의 판이 다르면 `note` 로 알린다.
 */
export function openDoc(raw: unknown): OpenResult {
  if (isNewFormat(raw)) return { doc: clone(raw), problem: null, name: null, note: null }
  if (isObject(raw) && isNewFormat(raw['payload'])) {
    const inner = raw['payload'].designerApplication.version
    const outer = raw['schema_version']
    const note =
      typeof outer === 'string' && outer !== inner
        ? `봉투 판(${outer}) ≠ 본문 판(${String(inner)})`
        : null
    return {
      doc: clone(raw['payload']),
      problem: null,
      name: typeof raw['name'] === 'string' ? raw['name'] : null,
      note,
    }
  }
  return { doc: null, problem: 'not_new_format', name: null, note: null }
}

/** 글자(파일 내용)를 연다 — JSON 이 아니면 `problem: 'not_json'` */
export function openText(text: string): OpenResult {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    return { doc: null, problem: 'not_json', name: null, note: null }
  }
  return openDoc(raw)
}

/* ── 자리 ──────────────────────────────────────────────────────────────── */

export type LocationKind = 'slot' | 'module' | 'area' | 'unknown'
export interface EditorLocation {
  kind: LocationKind
  value: string | null
}

/** 셋업 자리 세 꼴을 한 모양으로 */
export function locationOf(params: Record<string, unknown>): EditorLocation {
  const loc = isObject(params['location']) ? params['location'] : {}
  if (typeof loc['slotName'] === 'string') return { kind: 'slot', value: loc['slotName'] }
  if (typeof loc['moduleId'] === 'string') return { kind: 'module', value: loc['moduleId'] }
  if (typeof loc['addressableAreaName'] === 'string')
    return { kind: 'area', value: loc['addressableAreaName'] }
  return { kind: 'unknown', value: null }
}

function toLocation(loc: EditorLocation): Record<string, string> {
  if (loc.value === null) throw new Error('location_empty')
  if (loc.kind === 'slot') return { slotName: loc.value }
  if (loc.kind === 'module') return { moduleId: loc.value }
  if (loc.kind === 'area') return { addressableAreaName: loc.value }
  throw new Error(`location_kind:${loc.kind}`)
}

/** 🔴 셋업 명령을 쓰는 유일한 자리(S-326). `fn(commands)` 가 제자리에서 고친다 */
export function writeSetup(doc: EditorDoc, fn: (commands: EditorCommand[]) => void): EditorDoc {
  const out = clone(doc)
  fn(out.commands)
  return out
}

const defUri = (params: Record<string, unknown>): string =>
  `${String(params['namespace'])}/${String(params['loadName'])}/${String(params['version'])}`

export interface Placement {
  labwareId: string
  displayName: string
  definitionUri: string
  location: EditorLocation
  wells: string[]
}

/** 랩웨어 배치 표 — 셋업 `loadLabware` 차례대로 */
export function placements(doc: EditorDoc): Placement[] {
  const defs = doc.labwareDefinitions ?? {}
  return doc.commands
    .filter((c) => c.commandType === 'loadLabware')
    .map((c) => {
      const uri = defUri(c.params)
      return {
        labwareId: String(c.params['labwareId']),
        displayName: scalarText(c.params['displayName'] ?? c.params['loadName']),
        definitionUri: uri,
        location: locationOf(c.params),
        wells: Object.keys(defs[uri]?.wells ?? {}),
      }
    })
}

/** 모듈 표 — 셋업 `loadModule` */
export function modules(
  doc: EditorDoc,
): { moduleId: string; model: string; location: EditorLocation }[] {
  return doc.commands
    .filter((c) => c.commandType === 'loadModule')
    .map((c) => ({
      moduleId: String(c.params['moduleId']),
      model: scalarText(c.params['model']),
      location: locationOf(c.params),
    }))
}

/** 랩웨어 하나의 자리를 바꾼다 — 그 명령의 `location` 만 */
export function setLocation(doc: EditorDoc, labwareId: string, loc: EditorLocation): EditorDoc {
  let hit = 0
  const out = writeSetup(doc, (cmds) => {
    for (const c of cmds) {
      if (c.commandType === 'loadLabware' && c.params['labwareId'] === labwareId) {
        c.params['location'] = toLocation(loc)
        hit += 1
      }
    }
  })
  if (hit !== 1) throw new Error(`labware_not_unique:${labwareId}:${String(hit)}`)
  return out
}

/** 편집층 모듈 이름 — 파일의 `process` 가 권위다 */
export function moduleNames(doc: EditorDoc): string[] {
  return Object.keys(dataOf(doc).process ?? {})
}

/** 값 어딘가에 그 문자열이 있는가 — 모듈 스텝 파라미터는 랩웨어를 칸 이름이 제각각인 자리에 싣는다 */
function mentions(value: unknown, id: string): boolean {
  if (value === id) return true
  if (Array.isArray(value)) return value.some((v) => mentions(v, id))
  if (isObject(value)) return Object.values(value).some((v) => mentions(v, id))
  return false
}

/** 그 랩웨어를 쓰는 곳 — 시약 자리 · 전 모듈의 스텝. 지울 수 있는지 가른다 */
export function usesOfLabware(doc: EditorDoc, labwareId: string): string[] {
  const uses: string[] = []
  for (const c of doc.commands) {
    if (c.commandType === 'loadLiquid' && c.params['labwareId'] === labwareId)
      uses.push(`liquid:${String(c.params['liquidId'])}`)
  }
  for (const m of moduleNames(doc)) {
    for (const kind of STEP_KINDS) {
      for (const s of steps(doc, m, kind)) if (mentions(s, labwareId)) uses.push(`${m}:${s.key}`)
    }
  }
  return uses
}

/**
 * 랩웨어를 더한다 — 정의는 파일 안 `labwareDefinitions` 에 있는 것만.
 * 셋업 `loadLabware` 를 마지막 `loadLabware` 뒤에 넣고, 편집층 `labware.<id>` 를 만든다(이미 있으면 둔다).
 */
export function addLabware(
  doc: EditorDoc,
  input: { labwareId: string; definitionUri: string; location: EditorLocation },
): EditorDoc {
  const def = (doc.labwareDefinitions ?? {})[input.definitionUri]
  if (def === undefined) throw new Error(`definition_missing:${input.definitionUri}`)
  if (input.labwareId === '') throw new Error('labware_id_empty')
  if (placements(doc).some((p) => p.labwareId === input.labwareId))
    throw new Error(`labware_exists:${input.labwareId}`)
  const out = writeSetup(doc, (cmds) => {
    const at = cmds.map((c) => c.commandType).lastIndexOf('loadLabware') + 1
    cmds.splice(at, 0, {
      commandType: 'loadLabware',
      intent: 'setup',
      key: `setup.load_labware.${input.labwareId}`,
      params: {
        labwareId: input.labwareId,
        loadName: def.parameters.loadName,
        namespace: def.namespace,
        version: def.version,
        location: toLocation(input.location),
        ...(def.metadata?.displayName === undefined
          ? {}
          : { displayName: def.metadata.displayName }),
      },
    })
  })
  const d = dataOf(out)
  d.labware = d.labware ?? {}
  const wells = Object.values(def.wells ?? {})
  const full = wells.some((w) => typeof w['x'] === 'number' && typeof w['y'] === 'number')
  if (d.labware[input.labwareId] === undefined)
    d.labware[input.labwareId] = {
      definitionUri: input.definitionUri,
      layout_detail: full ? 'full' : 'simple',
    }
  return out
}

export const TYPE_TAG = 'ctcells.type:'

/**
 * 템플릿 랩웨어를 불러온다 — 정의를 **복사**해 넣고(같은 URI 가 이미 있으면 그것을 쓴다) 자리에 놓는다.
 * 레시피는 템플릿을 참조하지 않는다. 종류는 정의 `metadata.tags` 에 `ctcells.type:<category>` 로 적는다(이미 있으면 둔다).
 */
export function importLabware(
  doc: EditorDoc,
  entry: { definition: unknown; category?: unknown },
  input: { labwareId: string; location: EditorLocation },
): EditorDoc {
  if (!isObject(entry.definition)) throw new Error('template_definition_missing')
  const def = clone(entry.definition) as unknown as LabwareDefinition
  const uri = `${def.namespace}/${def.parameters.loadName}/${String(def.version)}`
  def.metadata = def.metadata ?? {}
  const tags = (def.metadata.tags = def.metadata.tags ?? [])
  if (
    typeof entry.category === 'string' &&
    !tags.some((tag) => typeof tag === 'string' && tag.startsWith(TYPE_TAG))
  )
    tags.push(TYPE_TAG + entry.category)
  const out = clone(doc)
  out.labwareDefinitions = out.labwareDefinitions ?? {}
  if (out.labwareDefinitions[uri] === undefined) out.labwareDefinitions[uri] = def
  return addLabware(out, {
    labwareId: input.labwareId,
    definitionUri: uri,
    location: input.location,
  })
}

/** 랩웨어를 뺀다 — 쓰는 곳이 있으면 거절한다(`uses`) */
export function removeLabware(
  doc: EditorDoc,
  labwareId: string,
): { doc: EditorDoc; uses: string[] } {
  const uses = usesOfLabware(doc, labwareId)
  if (uses.length > 0) return { doc, uses }
  const out = writeSetup(doc, (cmds) => {
    const i = cmds.findIndex(
      (c) => c.commandType === 'loadLabware' && c.params['labwareId'] === labwareId,
    )
    if (i >= 0) cmds.splice(i, 1)
  })
  delete dataOf(out).labware?.[labwareId]
  return { doc: out, uses: [] }
}

/* ── 시약 · 시료 ───────────────────────────────────────────────────────── */

export const TRAY_TYPE = 'hypicc_chamber_tray'
export const REFERENCE_PREFIX = 'REF'

/** 하이픽 챔버 트레이 labware id — 정의 태그가 `ctcells.type:hypicc_chamber_tray` 인 것(서버 `labware.context_of` 와 같은 규칙) */
function trayIds(doc: EditorDoc): Set<string> {
  const defs = doc.labwareDefinitions ?? {}
  return new Set(
    Object.entries(dataOf(doc).labware ?? {})
      .filter(([, lw]) =>
        (defs[lw.definitionUri ?? '']?.metadata?.tags ?? []).includes(TYPE_TAG + TRAY_TYPE),
      )
      .map(([id]) => id),
  )
}

export interface LiquidRow {
  liquidId: string
  displayName: string
  description: string
  displayColor: string
  /** 트레이에 적재된 액체 = 시료다 — 시약 표는 이것을 뺀다 */
  sample: boolean
  loads: { labwareId: string; volumeByWell: Record<string, number> }[]
}

const volumesOf = (params: Record<string, unknown>): Record<string, number> => {
  const raw = params['volumeByWell']
  if (!isObject(raw)) return {}
  return Object.fromEntries(Object.entries(raw).map(([w, v]) => [w, Number(v)]))
}

/** 액체 표 — `liquids` 와 그 자리(`loadLiquid`)를 묶는다 */
export function liquids(doc: EditorDoc): LiquidRow[] {
  const trays = trayIds(doc)
  return Object.entries(doc.liquids ?? {}).map(([id, l]) => {
    const loads = doc.commands
      .filter((c) => c.commandType === 'loadLiquid' && c.params['liquidId'] === id)
      .map((c) => ({ labwareId: String(c.params['labwareId']), volumeByWell: volumesOf(c.params) }))
    return {
      liquidId: id,
      displayName: l.displayName ?? '',
      description: l.description ?? '',
      displayColor: l.displayColor ?? '',
      sample: loads.some((ld) => trays.has(ld.labwareId)),
      loads,
    }
  })
}

export interface SampleRow {
  sampleRef: string
  role: 'reference' | 'patient'
  labwareId: string
  wells: string[]
}

/** 시료 자리 표 — 트레이 `loadLiquid` 하나가 묶음 하나(적재 차례 · 웰은 적은 차례 · id 머리 `REF` = 레퍼런스) */
export function samples(doc: EditorDoc): SampleRow[] {
  const trays = trayIds(doc)
  return doc.commands
    .filter((c) => c.commandType === 'loadLiquid' && trays.has(String(c.params['labwareId'])))
    .map((c) => {
      const ref = String(c.params['liquidId'])
      return {
        sampleRef: ref,
        role: ref.startsWith(REFERENCE_PREFIX) ? 'reference' : 'patient',
        labwareId: String(c.params['labwareId']),
        wells: Object.keys(volumesOf(c.params)),
      }
    })
}

/** 시약 칸(이름 · 설명 · 색)을 고친다 — 준 칸만. 빈 값이면 그 칸을 뺀다 */
export function setLiquid(
  doc: EditorDoc,
  liquidId: string,
  patch: Partial<Record<'displayName' | 'description' | 'displayColor', string>>,
): EditorDoc {
  const out = clone(doc)
  const l = out.liquids?.[liquidId]
  if (l === undefined) throw new Error(`liquid_missing:${liquidId}`)
  for (const [k, v] of Object.entries(patch)) {
    if (v === '' || v === undefined) delete l[k]
    else l[k] = v
  }
  return out
}

/** 시약 lot(편집층 `reagents.<id>.lot` · 선택) — 없으면 빈 글자 */
export function reagentLot(doc: EditorDoc, liquidId: string): string {
  const lot = dataOf(doc).reagents?.[liquidId]?.['lot']
  return typeof lot === 'string' ? lot : ''
}

/** 시약 lot 을 적는다 — 빈 값이면 칸을 뺀다. 편집층에 그 시약 항목이 없으면 거절한다 */
export function setReagentLot(doc: EditorDoc, liquidId: string, lot: string): EditorDoc {
  const out = clone(doc)
  const entry = dataOf(out).reagents?.[liquidId]
  if (entry === undefined) throw new Error(`reagent_entry_missing:${liquidId}`)
  const value = lot.trim()
  if (value === '') delete entry['lot']
  else entry['lot'] = value
  return out
}

/** 시약을 더한다 — 새 번호는 지금 쓰는 번호의 다음 */
export function addLiquid(
  doc: EditorDoc,
  input: { displayName: string; description?: string },
): { doc: EditorDoc; liquidId: string } {
  const out = clone(doc)
  out.liquids = out.liquids ?? {}
  const ids = Object.keys(out.liquids).map(Number).filter(Number.isFinite)
  const id = String((ids.length > 0 ? Math.max(...ids) : 0) + 1)
  out.liquids[id] =
    input.description === undefined || input.description === ''
      ? { displayName: input.displayName }
      : { displayName: input.displayName, description: input.description }
  return { doc: out, liquidId: id }
}

/** 템플릿 시약을 불러온다 — 이름 · 설명 · 색을 **복사**해 새 시약 하나로 */
export function importReagent(
  doc: EditorDoc,
  entry: { display_name?: unknown; description?: unknown; display_color?: unknown },
): { doc: EditorDoc; liquidId: string } {
  const r = addLiquid(doc, {
    displayName: typeof entry.display_name === 'string' ? entry.display_name : '',
    description: typeof entry.description === 'string' ? entry.description : '',
  })
  const liquid = r.doc.liquids?.[r.liquidId]
  if (liquid !== undefined && typeof entry.display_color === 'string')
    liquid.displayColor = entry.display_color
  return r
}

/** 그 시약을 쓰는 염색 스텝 키 */
export function usesOfLiquid(doc: EditorDoc, liquidId: string): string[] {
  return steps(doc)
    .filter((s) => s['liquidId'] === liquidId)
    .map((s) => s.key)
}

/** 시약을 뺀다 — 스텝이 쓰면 거절한다(`uses`). 자리(`loadLiquid`)와 편집층 칸도 함께 뺀다 */
export function removeLiquid(doc: EditorDoc, liquidId: string): { doc: EditorDoc; uses: string[] } {
  const uses = usesOfLiquid(doc, liquidId)
  if (uses.length > 0) return { doc, uses }
  let out = clone(doc)
  delete out.liquids?.[liquidId]
  out = writeSetup(out, (cmds) => {
    for (let i = cmds.length - 1; i >= 0; i -= 1) {
      const c = cmds[i]
      if (c?.commandType === 'loadLiquid' && c.params['liquidId'] === liquidId) cmds.splice(i, 1)
    }
  })
  delete dataOf(out).reagents?.[liquidId]
  return { doc: out, uses: [] }
}

/** 웰 하나의 충전량(µL)을 정한다 — `null` · 0 이면 그 웰을 뺀다. 그 랩웨어의 자리 명령이 없으면 만든다 */
export function setVolume(
  doc: EditorDoc,
  liquidId: string,
  labwareId: string,
  well: string,
  volume: number | null,
): EditorDoc {
  if (doc.liquids?.[liquidId] === undefined) throw new Error(`liquid_missing:${liquidId}`)
  if (well === '') throw new Error('well_empty')
  return writeSetup(doc, (cmds) => {
    let c = cmds.find(
      (x) =>
        x.commandType === 'loadLiquid' &&
        x.params['liquidId'] === liquidId &&
        x.params['labwareId'] === labwareId,
    )
    if (c === undefined && volume) {
      c = {
        commandType: 'loadLiquid',
        intent: 'setup',
        key: `setup.load_liquid.${liquidId}.${labwareId}`,
        params: { liquidId, labwareId, volumeByWell: {} },
      }
      const types = cmds.map((x) => x.commandType)
      const at = types.lastIndexOf('loadLiquid') + 1 || types.lastIndexOf('loadLabware') + 1
      cmds.splice(at, 0, c)
    }
    if (c === undefined) return
    const byWell = isObject(c.params['volumeByWell']) ? c.params['volumeByWell'] : {}
    c.params['volumeByWell'] = byWell
    if (volume) byWell[well] = volume
    else delete byWell[well]
    if (Object.keys(byWell).length === 0) cmds.splice(cmds.indexOf(c), 1)
  })
}

/* ── 스텝 ──────────────────────────────────────────────────────────────── */

export const STEP_KINDS = ['liquid_steps', 'module_steps'] as const
export type StepKind = (typeof STEP_KINDS)[number]

function stepList(doc: EditorDoc, module: string, kind: StepKind): EditorStep[] {
  const block = dataOf(doc).process?.[module]
  if (block === undefined) throw new Error(`process_missing:${module}`)
  if (!Array.isArray(block[kind])) block[kind] = []
  return block[kind] as EditorStep[]
}

/** 스텝 목록 — `kind` 는 `liquid_steps`(염색) 또는 `module_steps` */
export function steps(
  doc: EditorDoc,
  module = 'staining',
  kind: StepKind = 'liquid_steps',
): EditorStep[] {
  const list = dataOf(doc).process?.[module]?.[kind]
  return Array.isArray(list) ? clone(list as EditorStep[]) : []
}

export interface StepTarget {
  module: string
  kind: StepKind
}

const DEFAULT_TARGET: StepTarget = { module: 'staining', kind: 'liquid_steps' }

/** 스텝을 `index` 자리에 넣는다(없으면 끝). 키가 겹치면 거절한다 */
export function addStep(
  doc: EditorDoc,
  step: EditorStep,
  target: StepTarget = DEFAULT_TARGET,
  index?: number,
): EditorDoc {
  if (step.key === '') throw new Error('step_key_empty')
  const out = clone(doc)
  const list = stepList(out, target.module, target.kind)
  if (list.some((s) => s.key === step.key)) throw new Error(`step_key_exists:${step.key}`)
  list.splice(index ?? list.length, 0, clone(step))
  return out
}

export function removeStep(
  doc: EditorDoc,
  index: number,
  target: StepTarget = DEFAULT_TARGET,
): EditorDoc {
  const out = clone(doc)
  const list = stepList(out, target.module, target.kind)
  if (index < 0 || index >= list.length) throw new Error(`step_index:${String(index)}`)
  list.splice(index, 1)
  return out
}

export function moveStep(
  doc: EditorDoc,
  from: number,
  to: number,
  target: StepTarget = DEFAULT_TARGET,
): EditorDoc {
  const out = clone(doc)
  const list = stepList(out, target.module, target.kind)
  if (from < 0 || from >= list.length || to < 0 || to >= list.length) throw new Error('step_index')
  const [moved] = list.splice(from, 1)
  if (moved !== undefined) list.splice(to, 0, moved)
  return out
}

/** 스텝 하나를 통째로 바꾼다(폼이 모은 값 — 폼은 받은 스텝 위에 고친 칸만 얹어 넘긴다) */
export function replaceStep(
  doc: EditorDoc,
  index: number,
  step: EditorStep,
  target: StepTarget = DEFAULT_TARGET,
): EditorDoc {
  const out = clone(doc)
  const list = stepList(out, target.module, target.kind)
  if (index < 0 || index >= list.length) throw new Error(`step_index:${String(index)}`)
  list[index] = clone(step)
  return out
}

/* ── 요약 · 다시 만들기 ────────────────────────────────────────────────── */

export interface CommandSummary {
  total: number
  setup: number
  work: number
  byType: Record<string, number>
}

/** 명령 목록 요약 — 셋업 · 작업 수와 종류별 수(문서 안 계산 · 서버 요약과 같은 규칙) */
export function commandSummary(doc: EditorDoc): CommandSummary {
  const byType: Record<string, number> = {}
  let setup = 0
  for (const c of doc.commands) {
    byType[c.commandType] = (byType[c.commandType] ?? 0) + 1
    if (isSetup(c)) setup += 1
  }
  return { total: doc.commands.length, setup, work: doc.commands.length - setup, byType }
}

/**
 * 서버가 다시 만든 문서에서 **작업 명령 · 주석만** 받는다 — 셋업 명령이 지금 문서와 같은 차례로 같지 않으면 거절한다
 * (셋업은 원본이다 · S-326). 나머지 칸은 지금 문서 그대로.
 */
export function replaceWork(doc: EditorDoc, regenerated: unknown): EditorDoc {
  if (!isNewFormat(regenerated)) throw new Error('regenerated_not_new_format')
  const setupOf = (d: EditorDoc) => JSON.stringify(d.commands.filter(isSetup))
  if (setupOf(doc) !== setupOf(regenerated)) throw new Error('regenerated_setup_differs')
  const out = clone(doc)
  out.commands = clone(regenerated.commands)
  out.commandAnnotations = clone(regenerated.commandAnnotations ?? [])
  return out
}

const MODULE_NAME: Readonly<Record<string, string>> = {
  staining: '염색',
  centrifuge: '원심',
  assembler: '어셈블러',
}

/** 검증 결과가 가리키는 자리(JSON Pointer)를 사람이 읽는 이름으로 — **보이기용**(판정 아님). 못 풀면 포인터 그대로 */
export function describeField(doc: EditorDoc, pointer: string): string {
  const parts = pointer
    .split('/')
    .slice(1)
    .map((p) => p.replace(/~1/g, '/').replace(/~0/g, '~'))
  const [d0, d1, d2, module, kind, index] = parts
  if (
    d0 === 'designerApplication' &&
    d1 === 'data' &&
    d2 === 'process' &&
    module !== undefined &&
    (kind === 'liquid_steps' || kind === 'module_steps')
  ) {
    const step = steps(doc, module, kind)[Number(index)]
    if (step !== undefined)
      return `${MODULE_NAME[module] ?? module} 스텝 ${String(Number(index) + 1)} (${step.key})${
        parts.length > 6 ? ` · ${parts.slice(6).join('.')}` : ''
      }`
  }
  if (parts[0] === 'commands') {
    const c = doc.commands[Number(parts[1])]
    if (c !== undefined)
      return `명령 ${String(Number(parts[1]) + 1)} (${c.commandType}${c.key === undefined ? '' : ` ${c.key}`})${
        parts.length > 2 ? ` · ${parts.slice(2).join('.')}` : ''
      }`
  }
  return pointer
}

/** 저장 판 — `schema_version` = 문서의 `designerApplication.version`(쓰는 쪽이 밝힌다 · 서버가 추측하지 않는다) */
export function schemaVersionOf(doc: EditorDoc): string {
  return doc.designerApplication.version ?? ''
}
